import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Linkify from "linkify-react";
import { MentionsInput, Mention } from "react-mentions";
import { useSelector } from "react-redux";

import {
  RenderIf,
  FileSlider,
  ClickMore,
  ImageGrid,
  Loading,
} from "shared/components";

import { Urls } from "shared/constants/url";
import Member from "shared/media/user/userIcon.png";
import { calculateTimeAgo } from "shared/helper/formatDate";
import { SendIcon, Like } from "shared/components/Icons";
import { renderLink } from "shared/helper/getYoutubeVideoId";
import { custom, customStyle } from "shared/helper/mentionStyle";

import {
  useDeleteDiscussionCommentsMutation,
  useUpdateDiscussionCommentsMutation,
  useDiscussionCommentLikeMutation,
  useDeleteDiscussionCommentLikeMutation,
} from "redux/api/discussion";

import { useGetAllUsersWithoutFilterQuery } from "redux/api/siteUsers";

import "./DiscussionComment.scss";

const { REACT_APP_BASE_PHOTO_URL_MAIN } = process.env;

const PostComments = ({
  siteUserId,
  description = "",
  userName,
  userSurname,
  userPhoto,
  createDate,
  newsCommentFiles = [],
  commentLoading,
  commentId,
  refetch,
  isLiked,
  likeCount,
  discussionId,
}) => {
  const { t } = useTranslation();
  const { id } = useSelector((state) => state.user.user);

  const { data: users } = useGetAllUsersWithoutFilterQuery();

  const [modal, setModal] = useState(false);
  const [text, setText] = useState(description || "");
  const [files, setFiles] = useState([]);
  const [deletedFiles, setDeletedFiles] = useState([]);

  const [deleteComment, { isSuccess: deleteSuccess }] =
    useDeleteDiscussionCommentsMutation();

  const [updateComment, { isSuccess: updateSuccess }] =
    useUpdateDiscussionCommentsMutation();

  const [commentLike] = useDiscussionCommentLikeMutation();
  const [deleteCommentLike] = useDeleteDiscussionCommentLikeMutation();

  const suggestions = useMemo(() => {
    return (
      users?.data?.map((user) => ({
        id: user.id,
        display: `${user.firstName} ${user.lastName}`,
      })) || []
    );
  }, [users]);

  const mappedFiles = useMemo(() => {
    return newsCommentFiles.map((file) => ({
      ...file,
      fileUrl: `${REACT_APP_BASE_PHOTO_URL_MAIN}${file.fileUrl}`,
    }));
  }, [newsCommentFiles]);

  const shownDescription = useMemo(() => {
    return suggestions.reduce((result, user) => {
      return result.replaceAll(`@${user.id}`, user.display);
    }, description || "");
  }, [description, suggestions]);

  const handleInputChange = (event, newValue) => {
    setText(newValue || "");
  };

  const onDeleteComment = (event) => {
    event.preventDefault();
    deleteComment(commentId);
  };

  const updatePostComment = () => {
    const formData = new FormData();

    formData.append("DiscussionId", discussionId);
    formData.append("DiscussionCommentId", commentId);
    formData.append("description", text || "");

    deletedFiles.forEach((id) => formData.append("DeletedFiles", id));
    files.forEach((file) => {
      if (!file?.id) formData.append("Files", file);
    });

    updateComment(formData);
  };

  const closeEdit = () => {
    setModal(false);
    setText(description || "");
    setFiles([]);
    setDeletedFiles([]);
  };

  useEffect(() => {
    if (!deleteSuccess && !updateSuccess) return;

    refetch?.();
    closeEdit();
  }, [deleteSuccess, updateSuccess]);

  const titles = [
    { id: "edit", title: t("edit"), onClick: () => setModal(true) },
    { id: "delete", title: t("delete"), onClick: onDeleteComment },
  ];

  return (
    <div className="cardMainCommentList">
      <RenderIf condition={!commentLoading} renderElse={<Loading />}>
        <div className="cardMainCommentListBox">
          <div className="cardMainCommentListBoxLeft">
            <Link to={`${Urls.POSTS_URL}/${siteUserId}`}>
              <img
                src={
                  userPhoto
                    ? `${REACT_APP_BASE_PHOTO_URL_MAIN}${userPhoto}`
                    : Member
                }
                alt="user"
              />
            </Link>
          </div>

          <div className="cardMainCommentListBoxRight">
            <RenderIf condition={!modal}>
              <div className="cardMainCommentListBoxRightInner noFile">
                <div className="d-flex justify-content-between align-items-center">
                  <Link to={`${Urls.POSTS_URL}/${siteUserId}`}>
                    <span className="name">
                      {userName} {userSurname}
                    </span>
                  </Link>

                  <div className="d-flex align-items-center">
                    <span className="time mx-2">
                      {calculateTimeAgo(createDate)}
                    </span>

                    <RenderIf condition={id === siteUserId}>
                      <ClickMore buttonTitles={titles} />
                    </RenderIf>
                  </div>
                </div>

                <RenderIf condition={shownDescription}>
                  <div className={newsCommentFiles.length ? "mb-3" : ""}>
                    <Linkify options={{ render: renderLink }}>
                      {shownDescription}
                    </Linkify>
                  </div>
                </RenderIf>

                <RenderIf condition={newsCommentFiles.length}>
                  <ImageGrid forComment>
                    {newsCommentFiles.map((file) =>
                      file.fileUrl.includes(".mp4") ? (
                        <video
                          key={file.id || file.fileUrl}
                          src={`${REACT_APP_BASE_PHOTO_URL_MAIN}${file.fileUrl}`}
                          controls
                        />
                      ) : (
                        <img
                          key={file.id || file.fileUrl}
                          src={`${REACT_APP_BASE_PHOTO_URL_MAIN}${file.fileUrl}`}
                          alt="comment"
                        />
                      ),
                    )}
                  </ImageGrid>
                </RenderIf>
              </div>
            </RenderIf>

            <RenderIf condition={modal}>
              <div className="cardMainCommentListBoxRightInner">
                <div className="d-flex justify-content-between align-items-center">
                  <Link to={`${Urls.POSTS_URL}/${siteUserId}`}>
                    <span className="name">
                      {userName} {userSurname}
                    </span>
                  </Link>
                </div>

                <MentionsInput
                  value={text || ""}
                  onChange={handleInputChange}
                  placeholder={t("writeComment")}
                  style={customStyle}
                >
                  <Mention
                    trigger="@"
                    data={suggestions}
                    markup="@[__display__](__id__)"
                    displayTransform={(id, display) => `@${display}`}
                    style={{ ...custom }}
                  />
                </MentionsInput>

                <div className="d-flex align-items-center justify-content-between mt-2">
                  <button type="button" onClick={closeEdit}>
                    {t("cancel")}
                  </button>

                  <SendIcon
                    onClick={updatePostComment}
                    width="20px"
                    height="20px"
                    className="mx-2 sendButton"
                  />
                </div>
              </div>

              <FileSlider
                forComment
                file={mappedFiles}
                multiple
                handleImageID={setDeletedFiles}
                handleImageFile={setFiles}
              />
            </RenderIf>

            <div
              onClick={
                isLiked
                  ? () => deleteCommentLike({ discussionCommentId: commentId })
                  : () => {
                      const formData = new FormData();
                      formData.append("DiscussionCommentId", commentId);
                      commentLike(formData);
                    }
              }
              className={`${isLiked ? "like-active" : ""} newsCardTwoIcon mt-1`}
            >
              <Like />
              <span>
                {t("like")} ({likeCount})
              </span>
            </div>
          </div>
        </div>
      </RenderIf>
    </div>
  );
};

export default PostComments;
